import React, { useEffect, useState } from 'react';
import { useIntegrations } from '../hooks/use-integrations';
import { IntegrationCard, IntegrationConnectModal } from '../components/integrations';
import type { Integration, IntegrationProvider } from '../lib/integration-types';

const PROVIDER_INFO: Record<IntegrationProvider, { name: string; icon: string; description: string }> = {
  slack: { name: 'Slack', icon: '💬', description: 'Send messages and receive events' },
  notion: { name: 'Notion', icon: '📝', description: 'Create and update pages' },
  github: { name: 'GitHub', icon: '🐙', description: 'Create issues and PRs' },
  hubspot: { name: 'HubSpot', icon: '🔶', description: 'CRM operations' },
  google_workspace: { name: 'Google Workspace', icon: '📁', description: 'Drive, Calendar, Gmail' },
};

export function IntegrationsHubPage() {
  const { integrations, providers, loading, error, fetchIntegrations, fetchProviders, createIntegration, deleteIntegration, connectIntegration, disconnectIntegration } = useIntegrations();
  const [showConnectModal, setShowConnectModal] = useState<IntegrationProvider | null>(null);
  const [connectingId, setConnectingId] = useState<string | null>(null);

  useEffect(() => {
    fetchIntegrations();
    fetchProviders();
  }, [fetchIntegrations, fetchProviders]);

  const handleConnect = async (provider: IntegrationProvider) => {
    setShowConnectModal(provider);
  };

  const handleCreateAndConnect = async (name: string, provider: IntegrationProvider) => {
    const integration = await createIntegration({ name, provider });
    setShowConnectModal(null);

    // Get OAuth URL
    const { url } = await connectIntegration(integration.id);
    setConnectingId(integration.id);

    // In production, redirect to url
    // For now, just show the URL
    alert(`OAuth URL: ${url}\n\nIn production, you would be redirected to authorize.`);
  };

  const handleDisconnect = async (id: string) => {
    if (confirm('Disconnect this integration?')) {
      await disconnectIntegration(id);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Delete this integration? This cannot be undone.')) {
      await deleteIntegration(id);
    }
  };

  if (loading && integrations.length === 0) return <div className="p-8 text-center">Loading...</div>;
  if (error) return <div className="p-8 text-red-600">{error}</div>;

  const connectedIntegrations = integrations.filter(i => i.status === 'connected');
  const disconnectedProviders = Object.keys(PROVIDER_INFO).filter(
    p => !integrations.some(i => i.provider === p && i.status === 'connected')
  ) as IntegrationProvider[];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4">
          <h1 className="text-3xl font-bold text-gray-900">Integrations Hub</h1>
          <p className="text-gray-600 mt-1">Connect your favorite tools and services</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 px-4">
        {/* Connected Integrations */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Connected Integrations ({connectedIntegrations.length})</h2>
          {connectedIntegrations.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              No integrations connected yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {connectedIntegrations.map(integration => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  onDisconnect={() => handleDisconnect(integration.id)}
                  onDelete={() => handleDelete(integration.id)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Available Integrations */}
        <section>
          <h2 className="text-lg font-semibold mb-4">Available Integrations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {disconnectedProviders.map(provider => {
              const info = PROVIDER_INFO[provider];
              return (
                <div key={provider} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{info.icon}</span>
                    <div>
                      <h3 className="font-semibold">{info.name}</h3>
                      <p className="text-sm text-gray-500">{info.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleConnect(provider)}
                    className="mt-3 w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                  >
                    Connect
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Connect Modal */}
        {showConnectModal && (
          <IntegrationConnectModal
            provider={showConnectModal}
            onConnect={(name) => handleCreateAndConnect(name, showConnectModal)}
            onCancel={() => setShowConnectModal(null)}
          />
        )}
      </main>
    </div>
  );
}
